import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, feedbackAPI, budgetAPI, bulkApproveAPI } from '../services/api';
import { Feedback, Project } from '../types';

const ManagerPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('approvals');
  const [budgets, setBudgets] = useState<any[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [selectedProjects, setSelectedProjects] = useState<Set<string>>(new Set());
  const [selectedBudgets, setSelectedBudgets] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const hasAccess = user && user.role === 'manager';

  useEffect(() => {
    if (hasAccess) loadData();
  }, [activeTab, hasAccess]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'approvals') {
        const [budgetsRes, projectsRes] = await Promise.all([
          budgetAPI.getAll(),
          adminAPI.getPendingProjects()
        ]);
        setBudgets((budgetsRes.data || []).filter((b: any) => !b.is_approved || b.status === 'pending'));
        setProjects((projectsRes.data || []).filter((p: Project) =>
          p.workflow_stage === 'county_validated' || p.approval_status === 'pending_manager_approval'
        ));
      } else if (activeTab === 'feedback-review') {
        const feedbackRes = await feedbackAPI.getAdmin();
        setFeedback(feedbackRes.data);
      }
    } catch (error) {
      setMessage('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const approveBudget = async (budgetId: string, action: string, reason?: string) => {
    try {
      await budgetAPI.approve(budgetId, { action, reason });
      setMessage(`Budget ${action}d successfully`);
      setSelectedBudgets(new Set());
      loadData();
    } catch { setMessage(`Failed to ${action} budget`); }
  };

  const approveProject = async (projectId: string, action: string, reason?: string) => {
    try {
      await adminAPI.approveProject(projectId, { action, reason });
      setMessage(`Project ${action}d successfully`);
      setSelectedProjects(new Set());
      loadData();
    } catch { setMessage(`Failed to ${action} project`); }
  };

  const bulkProjectAction = async (action: 'approve' | 'reject') => {
    if (selectedProjects.size === 0) { setMessage('No projects selected'); return; }
    if (!window.confirm(`${action === 'approve' ? 'Approve' : 'Reject'} ${selectedProjects.size} project(s)?`)) return;
    try {
      setBulkLoading(true);
      await bulkApproveAPI.approve(Array.from(selectedProjects), action);
      setMessage(`${selectedProjects.size} project(s) ${action}d successfully`);
      setSelectedProjects(new Set());
      loadData();
    } catch { setMessage(`Failed to bulk ${action} projects`); }
    finally { setBulkLoading(false); }
  };

  const toggleSelectProject = (id: string) => {
    setSelectedProjects(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };
  const toggleSelectAll = () => {
    setSelectedProjects(selectedProjects.size === projects.length ? new Set() : new Set(projects.map(p => p.id)));
  };

  const moderateFeedback = async (feedbackId: string, status: string, isPublic: boolean) => {
    try {
      await feedbackAPI.moderate(feedbackId, { status, is_public: isPublic });
      setMessage('Feedback moderated successfully');
      loadData();
    } catch { setMessage('Failed to moderate feedback'); }
  };

  const totalPending = budgets.length + projects.length;

  if (!hasAccess) {
    return (
      <div className="manager-page">
        <header className="page-header">
          <div className="icon icon-primary">🔒</div>
          <h1>Manager Access Required</h1>
          <p>Only Managers can access this page</p>
        </header>
        <div className="access-denied">
          <p>This page is restricted to Managers only.</p>
          <button onClick={() => navigate('/login')} className="btn btn-primary">Staff Login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="manager-page">
      <header className="page-header">
        <div className="icon icon-primary">👨‍💼</div>
        <h1>Manager Dashboard</h1>
        <p>Oversight & Governance — Ensure data accuracy, credibility, and integrity</p>
      </header>

      {message && (
        <div className={`message ${message.includes('successfully') ? 'success' : 'error'}`}
          onClick={() => setMessage('')} style={{ cursor: 'pointer' }}>
          {message}
        </div>
      )}

      <div className="manager-tabs">
        <div className="tabs-container">
          <button className={`tab-button ${activeTab === 'approvals' ? 'active' : ''}`} onClick={() => setActiveTab('approvals')}>
            ✅ Approvals {totalPending > 0 && <span style={{ background: '#e53935', color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '0.75rem', marginLeft: '4px' }}>{totalPending}</span>}
          </button>
          <button className={`tab-button ${activeTab === 'feedback-review' ? 'active' : ''}`} onClick={() => setActiveTab('feedback-review')}>
            📝 Feedback Review
          </button>
          <button className={`tab-button ${activeTab === 'reports' ? 'active' : ''}`} onClick={() => setActiveTab('reports')}>
            📈 Reports
          </button>
        </div>
      </div>

      <div className="manager-content">

        {/* ── CONSOLIDATED APPROVALS TAB ── */}
        {activeTab === 'approvals' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">✅</div>
              <div>
                <h2>Pending Approvals</h2>
                <p>Review and approve budget documents and project allocations submitted by institutional responders.</p>
              </div>
            </div>

            {loading ? <div className="loading">Loading pending approvals...</div> : (
              <>
                {/* Summary badges */}
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                  <div className="stat-card" style={{ flex: 1, minWidth: '140px', borderLeft: '4px solid #3E6892' }}>
                    <h3 style={{ color: '#3E6892' }}>{budgets.length}</h3>
                    <p>Budget Documents</p>
                  </div>
                  <div className="stat-card" style={{ flex: 1, minWidth: '140px', borderLeft: '4px solid #f59e0b' }}>
                    <h3 style={{ color: '#f59e0b' }}>{projects.length}</h3>
                    <p>Project Allocations</p>
                  </div>
                  <div className="stat-card" style={{ flex: 1, minWidth: '140px', borderLeft: '4px solid #10b981' }}>
                    <h3 style={{ color: '#10b981' }}>{totalPending}</h3>
                    <p>Total Pending</p>
                  </div>
                </div>

                {totalPending === 0 ? (
                  <div className="empty-state"><p>✅ All caught up — no pending approvals.</p></div>
                ) : (
                  <>
                    {/* ── BUDGET DOCUMENTS ── */}
                    {budgets.length > 0 && (
                      <div style={{ marginBottom: '2rem' }}>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #e0e0e0' }}>
                          📄 Budget Documents ({budgets.length})
                        </h3>
                        <div className="budget-list">
                          {budgets.map(budget => (
                            <div key={budget.id} className="budget-item">
                              <div className="budget-header">
                                <h3>{budget.county} County Budget {budget.fiscal_year}</h3>
                                <span className="budget-amount">KES {(budget.total_budget / 1e9).toFixed(2)}B</span>
                              </div>
                              <div className="budget-details">
                                <p><strong>Uploaded by:</strong> {budget.uploaded_by || 'Institutional Responder'}</p>
                                <p><strong>Date:</strong> {new Date(budget.uploaded_at).toLocaleDateString()}</p>
                                <p><strong>Status:</strong> <span className={`status-badge ${budget.status}`}>{budget.status}</span></p>
                              </div>
                              <div className="budget-actions">
                                <button onClick={() => approveBudget(budget.id, 'approve')} className="approve-btn">✅ Approve</button>
                                <button onClick={() => { const r = prompt('Reason for rejection:'); if (r) approveBudget(budget.id, 'reject', r); }} className="reject-btn">❌ Reject</button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* ── PROJECT ALLOCATIONS ── */}
                    {projects.length > 0 && (
                      <div>
                        <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid #e0e0e0' }}>
                          🏗️ Project Allocations ({projects.length})
                        </h3>

                        {/* Bulk toolbar */}
                        <div style={{ display: 'flex', gap: '0.8rem', alignItems: 'center', marginBottom: '1rem', padding: '0.8rem', background: '#f5f5f5', borderRadius: '8px', flexWrap: 'wrap' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', cursor: 'pointer', fontWeight: 600 }}>
                            <input type="checkbox" checked={selectedProjects.size === projects.length && projects.length > 0} onChange={toggleSelectAll} />
                            Select All ({projects.length})
                          </label>
                          <span style={{ color: '#666', fontSize: '0.9rem' }}>{selectedProjects.size} selected</span>
                          <button className="approve-btn" disabled={selectedProjects.size === 0 || bulkLoading} onClick={() => bulkProjectAction('approve')} style={{ opacity: selectedProjects.size === 0 ? 0.5 : 1 }}>
                            {bulkLoading ? 'Processing...' : `✅ Approve Selected (${selectedProjects.size})`}
                          </button>
                          <button className="reject-btn" disabled={selectedProjects.size === 0 || bulkLoading} onClick={() => bulkProjectAction('reject')} style={{ opacity: selectedProjects.size === 0 ? 0.5 : 1 }}>
                            ❌ Reject Selected
                          </button>
                        </div>

                        <div className="project-list">
                          {projects.map(project => (
                            <div key={project.id} className="project-item" style={{ display: 'flex', gap: '0.8rem', alignItems: 'flex-start' }}>
                              <input type="checkbox" checked={selectedProjects.has(project.id)} onChange={() => toggleSelectProject(project.id)}
                                style={{ marginTop: '0.4rem', width: '16px', height: '16px', cursor: 'pointer', flexShrink: 0 }} />
                              <div style={{ flex: 1 }}>
                                <div className="project-header">
                                  <h3>{project.name}</h3>
                                  <span className="project-budget">KES {(project.budget / 1e6).toFixed(2)}M</span>
                                </div>
                                <div className="project-details">
                                  <p><strong>Sector:</strong> {project.sector}{project.sub_location ? ` • 📍 ${project.sub_location}` : ''}</p>
                                  <p><strong>Ward:</strong> {project.ward_id}</p>
                                  {project.description && <p><strong>Description:</strong> {project.description}</p>}
                                </div>
                                <div className="project-actions">
                                  <button onClick={() => approveProject(project.id, 'approve')} className="approve-btn">✅ Approve</button>
                                  <button onClick={() => { const r = prompt('Reason for rejection:'); if (r) approveProject(project.id, 'reject', r); }} className="reject-btn">❌ Reject</button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </section>
        )}

        {/* ── FEEDBACK REVIEW ── */}
        {activeTab === 'feedback-review' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">📝</div>
              <div>
                <h2>Citizen Feedback Review</h2>
                <p>Monitor citizen reports and complaints to ensure transparency and accountability.</p>
              </div>
            </div>
            {loading ? <div className="loading">Loading citizen feedback...</div> : (
              <div className="feedback-list">
                {feedback.length === 0 ? (
                  <div className="empty-state"><p>No feedback submissions found.</p></div>
                ) : (
                  feedback.map(item => (
                    <div key={item.id} className="feedback-item">
                      <div className="feedback-header">
                        <div className="feedback-info">
                          <strong>{item.is_anonymous ? 'Anonymous' : item.name}</strong>
                          <span className="feedback-type">{item.feedback_type}</span>
                          <span className={`status-badge ${item.status}`}>{item.status}</span>
                        </div>
                        <div className="feedback-date">{new Date(item.created_at).toLocaleDateString()}</div>
                      </div>
                      <div className="feedback-details">
                        <p><strong>County:</strong> {item.county}</p>
                        {item.ward && <p><strong>Ward:</strong> {item.ward}</p>}
                        <p><strong>Message:</strong> {item.message}</p>
                      </div>
                      <div className="feedback-actions">
                        <button onClick={() => moderateFeedback(item.id, 'verified', true)} className="approve-btn">✅ Approve & Publish</button>
                        <button onClick={() => moderateFeedback(item.id, 'verified', false)} className="approve-private-btn">🔒 Approve (Private)</button>
                        <button onClick={() => moderateFeedback(item.id, 'rejected', false)} className="reject-btn">❌ Reject</button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </section>
        )}

        {/* ── REPORTS ── */}
        {activeTab === 'reports' && (
          <section className="content-section">
            <div className="section-header">
              <div className="icon icon-primary">📈</div>
              <div>
                <h2>Analytical Reports</h2>
                <p>Generate reports to ensure data matches official documents and maintain oversight.</p>
              </div>
            </div>
            <div className="reports-grid">
              <div className="report-card">
                <h3>Budget Compliance Report</h3>
                <p>Verify that project allocations match approved budget documents</p>
                <button className="btn btn-primary">Generate Report</button>
              </div>
              <div className="report-card">
                <h3>Citizen Feedback Summary</h3>
                <p>Analysis of citizen reports and complaints by category</p>
                <button className="btn btn-primary">Generate Report</button>
              </div>
              <div className="report-card">
                <h3>Data Integrity Audit</h3>
                <p>Ensure published data maintains accuracy and credibility</p>
                <button className="btn btn-primary">Generate Report</button>
              </div>
              <div className="report-card">
                <h3>Oversight Dashboard</h3>
                <p>Comprehensive view of platform data and approval workflows</p>
                <button className="btn btn-primary">View Dashboard</button>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ManagerPage;
